/**
 * Org-scoped template variables — the API client.
 *
 * Contract: backend PR #1220 (`/api/template-variables`). The registry holds
 * three kinds of variable, all usable as `{{key}}` in a message or a letter
 * and as `data_source: "variable.<key>"` on a form field:
 *   - system        platform variables (org_id null), never editable;
 *   - org           the org's own variables, editable by org admins;
 *   - custom_field  the org's visible custom fields, exposed automatically.
 *
 * Every call goes through apiClient, which adds the bearer token and X-CBO-ID.
 */

import { apiClient } from '@/services/apiClient';

export type VariableScope = 'system' | 'org' | 'custom_field';
export type SourceKind = 'static' | 'member_field' | 'program_field' | 'custom_field' | 'input' | 'system';
export type ValueType = 'text' | 'date' | 'datetime' | 'number' | 'link' | 'select' | 'boolean';
export type Fallback = 'block' | 'default' | 'empty';
export type AppliesTo = 'messages' | 'forms' | 'both';
/** Surfaces validate / preview accept: applies_to values plus `letters`
 *  (letters render every variable, including the mailing-address fields). */
export type CheckSurface = AppliesTo | 'letters';

export const SOURCE_KINDS: Exclude<SourceKind, 'system'>[] = [
  'static',
  'member_field',
  'program_field',
  'custom_field',
  'input',
];
export const VALUE_TYPES: ValueType[] = ['text', 'date', 'datetime', 'number', 'link', 'select', 'boolean'];
export const FALLBACKS: Fallback[] = ['block', 'default', 'empty'];
export const APPLIES_TO: AppliesTo[] = ['messages', 'forms', 'both'];

/** source_config.field values the backend accepts for member_field. */
export const MEMBER_FIELDS = [
  'first_name',
  'last_name',
  'email',
  'phone_no',
  'street_address',
  'address_line2',
  'city',
  'state',
  'zip_code',
  'county',
  'preferred_language',
  'member_number',
  'medicaid_id',
  'dob',
] as const;

/** source_config.field values the backend accepts for program_field. */
export const PROGRAM_FIELDS = [
  'name',
  'description',
  'program_type',
  'program_manager_name',
  'program_manager_email',
  'program_manager_phone',
  'start_date',
  'end_date',
] as const;

/** `^[a-z][a-z0-9_]{0,63}$` — the key rule the backend enforces. */
export const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

export interface SourceConfig {
  value?: string;
  field?: string;
  field_name?: string;
  prompt?: string;
  options?: string[];
  placeholder?: string;
}

export interface TemplateVariable {
  id: number | null;
  key: string;
  scope: VariableScope;
  org_id: number | null;
  label: string;
  description: string | null;
  category: string;
  value_type: ValueType;
  source_kind: SourceKind;
  source_config: SourceConfig | null;
  default_value: string | null;
  fallback: Fallback;
  applies_to: AppliesTo;
  active: boolean;
  editable: boolean;
  requires: string | null;
  placeholder: string;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface VariableListResponse {
  variables: TemplateVariable[];
  counts: Partial<Record<VariableScope, number>>;
}

export type UnresolvedReason =
  | 'unknown_variable'
  | 'no_value'
  | 'needs_context'
  | 'needs_input'
  | 'invalid_input'
  | 'inactive'
  | 'not_available_here';

export interface UnresolvedVariable {
  key: string;
  reason: UnresolvedReason | string;
  message: string;
}

export interface RequiredInput {
  key: string;
  label: string;
  value_type: ValueType;
  prompt: string | null;
  options: string[] | null;
  required: boolean;
  default_value: string | null;
}

export interface ValidateResponse {
  ok: boolean;
  variables: string[];
  errors: UnresolvedVariable[];
  warnings: UnresolvedVariable[];
  required_inputs: RequiredInput[];
}

export interface PreviewContext {
  program_id?: number | null;
  enrollment_id?: number | null;
  form_template_id?: number | null;
  form_instance_id?: number | null;
  appointment_id?: number | null;
  calendar_event_id?: number | null;
  service_referral_id?: number | null;
}

export interface PreviewRequest {
  subject?: string | null;
  body: string;
  member_id?: number | null;
  applies_to: CheckSurface;
  context?: PreviewContext;
  variable_inputs?: Record<string, string>;
}

export interface PreviewResponse {
  subject: string | null;
  body: string;
  variables: string[];
  unresolved: UnresolvedVariable[];
  required_inputs: RequiredInput[];
  can_send: boolean;
  sample_member: boolean;
}

export interface HistoryEntry {
  id: number;
  action: 'created' | 'updated' | 'deleted' | string;
  field_changes: Record<string, { old: unknown; new: unknown }> | null;
  performed_by_name: string | null;
  performed_at: string | null;
  notes: string | null;
  version_number: number | null;
  entity_name: string | null;
}

export interface VariableWriteBody {
  key?: string;
  label?: string;
  description?: string | null;
  category?: string;
  value_type?: ValueType;
  source_kind?: Exclude<SourceKind, 'system'>;
  source_config?: SourceConfig;
  default_value?: string | null;
  fallback?: Fallback;
  applies_to?: AppliesTo;
  active?: boolean;
}

/** One API error, normalised from either the `{detail: {code, message, field}}`
 *  shape or FastAPI's `{detail: [...]}` validation list. */
export interface TemplateVariableApiError {
  status: number;
  code: string | null;
  message: string;
  field: string | null;
}

export function parseApiError(err: unknown): TemplateVariableApiError {
  const anyErr = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
  const status = anyErr?.response?.status ?? 0;
  const detail = anyErr?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const d = detail as { code?: string; message?: string; field?: string };
    return { status, code: d.code ?? null, message: d.message ?? '', field: d.field ?? null };
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string; loc?: unknown[] };
    const loc = Array.isArray(first?.loc) ? first.loc : [];
    const field = loc.length > 1 ? String(loc[loc.length - 1]) : null;
    return { status, code: 'request_validation', message: first?.msg ?? '', field };
  }
  if (typeof detail === 'string') return { status, code: null, message: detail, field: null };
  return { status, code: null, message: anyErr?.message ?? '', field: null };
}

const BASE = '/api/template-variables';

/** A backend that predates the `letters` surface answers 422; retry as `messages`. */
async function withLettersFallback<B extends { applies_to: CheckSurface }, R>(body: B, call: (b: B) => Promise<R>): Promise<R> {
  try {
    return await call(body);
  } catch (err) {
    if (body.applies_to === 'letters' && parseApiError(err).status === 422) {
      return call({ ...body, applies_to: 'messages' });
    }
    throw err;
  }
}

export const templateVariablesApi = {
  async list(params: { applies_to?: AppliesTo; scope?: VariableScope; include_inactive?: boolean } = {}) {
    const { data } = await apiClient.get<VariableListResponse>(BASE, { params });
    return data;
  },
  async get(id: number) {
    const { data } = await apiClient.get<TemplateVariable>(`${BASE}/${id}`);
    return data;
  },
  async history(id: number) {
    const { data } = await apiClient.get<{ history: HistoryEntry[] }>(`${BASE}/${id}/history`);
    return data.history ?? [];
  },
  async create(body: VariableWriteBody) {
    const { data } = await apiClient.post<TemplateVariable>(BASE, body);
    return data;
  },
  async update(id: number, body: VariableWriteBody) {
    const { data } = await apiClient.patch<TemplateVariable>(`${BASE}/${id}`, body);
    return data;
  },
  async remove(id: number) {
    await apiClient.delete(`${BASE}/${id}`);
  },
  async preview(body: PreviewRequest) {
    return withLettersFallback(body, async (b) => (await apiClient.post<PreviewResponse>(`${BASE}/preview`, b)).data);
  },
  async validate(body: { subject?: string | null; body: string; applies_to: CheckSurface }) {
    return withLettersFallback(body, async (b) => (await apiClient.post<ValidateResponse>(`${BASE}/validate`, b)).data);
  },
  async requiredInputs(params: { form_template_id?: number; communication_template_id?: number }) {
    const { data } = await apiClient.get<{ required_inputs: RequiredInput[]; variables: string[] }>(
      `${BASE}/required-inputs`,
      { params },
    );
    return data;
  },
};

/** A failure entry from a member send (`failed[]`) that names the unfilled variables. */
export interface UnfilledSendFailure {
  member_id: number;
  error?: string;
  reason?: string;
  unresolved?: UnresolvedVariable[];
}

/** The failures a send refused because a merge field could not be filled. */
export function unfilledFailures(failed: unknown): UnfilledSendFailure[] {
  if (!Array.isArray(failed)) return [];
  return (failed as UnfilledSendFailure[]).filter(
    (f) =>
      f &&
      (f.reason === 'unfilled_merge_fields' ||
        (typeof f.error === 'string' && f.error.startsWith('Merge fields could not be filled'))),
  );
}

/** Drop empty values so an untouched input does not count as entered. */
export function cleanInputs(values: Record<string, string>): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    if (v !== undefined && v !== null && String(v).trim() !== '') out[k] = String(v);
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * TRUS-719: a public social post renders only org-scoped variables; the
 * backend rejects member-specific ones on save with a validation error naming
 * them. This pulls that error out of whatever shape it arrives in — an
 * axios error or an ApiError (`{status, data}`), with `detail` as an object
 * (`{code, message, variables|keys|invalid_variables}`), a FastAPI list or a
 * string — and returns null for any other failure.
 */
export interface NamedVariableRejection {
  message: string;
  keys: string[];
}

export function namedVariableRejection(err: unknown): NamedVariableRejection | null {
  const e = err as { response?: { status?: number; data?: unknown }; status?: number; data?: unknown };
  const status = e?.response?.status ?? e?.status ?? 0;
  if (status !== 400 && status !== 422) return null;
  const data = (e?.response?.data ?? e?.data) as { detail?: unknown } | undefined;
  const detail = data?.detail ?? data;
  let message = '';
  let code = '';
  let field = '';
  let keys: string[] = [];
  const takeKeys = (v: unknown) => {
    if (Array.isArray(v)) keys = v.filter((k): k is string => typeof k === 'string' && !!k.trim());
  };
  if (typeof detail === 'string') {
    message = detail;
  } else if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: string; loc?: unknown[]; ctx?: Record<string, unknown> } | undefined;
    message = first?.msg ?? '';
    const loc = first && Array.isArray(first.loc) ? first.loc : [];
    field = loc.length ? String(loc[loc.length - 1]) : '';
    takeKeys(first?.ctx?.variables ?? first?.ctx?.keys);
  } else if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    message = typeof d.message === 'string' ? d.message : typeof d.msg === 'string' ? d.msg : '';
    code = typeof d.code === 'string' ? d.code : '';
    field = typeof d.field === 'string' ? d.field : '';
    takeKeys(d.variables ?? d.keys ?? d.invalid_variables ?? d.member_variables);
  }
  const aboutVariables =
    keys.length > 0 || /variable/i.test(code) || field === 'social_content' || /variable|\{\{/i.test(message);
  if (!aboutVariables || (!message && keys.length === 0)) return null;
  if (keys.length === 0) {
    // "{{first_name}}" tokens named in the message itself.
    keys = Array.from(new Set(Array.from(message.matchAll(/\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g), (m) => m[1])));
  }
  return { message, keys };
}
